import { Button, Layout, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { AiOutlineReload } from "react-icons/ai";

import { DataTable } from "design-system/components/DataTable";
import { QueryErrorAlert } from "design-system/components/QueryErrorAlert";

import type { SandboxAdminTableLayoutProps } from "./types";

import s from "./SandboxAdminTableLayout.module.css";

const { Header, Content } = Layout;
const { Text } = Typography;

export const SandboxAdminTableLayout = <T,>({
  title,
  subtitle,
  tableTitle,
  data,
  columns,
  loading,
  rowKey,
  errorMessage,
  onRetry,
  retryLoading = false,
  toolbar,
  renderEmpty,
  rightActions,
  searchInput,
  testId,
  refreshTestId,
}: SandboxAdminTableLayoutProps<T>) => {
  const { t } = useTranslation("common");

  const defaultRightActions = [
    <Button
      key="refresh"
      icon={<AiOutlineReload />}
      onClick={onRetry}
      loading={retryLoading}
      data-testid={refreshTestId}
    >
      {t("components.buttons.refresh")}
    </Button>,
  ];

  return (
    <Layout>
      <Header className={s.header}>
        <Typography.Title level={3}>{title}</Typography.Title>
        <Text type="secondary">{subtitle}</Text>
      </Header>
      <Content className={s.content}>
        {toolbar && <div className={s.toolbar}>{toolbar}</div>}
        {errorMessage ? (
          <QueryErrorAlert
            description={errorMessage}
            onRetry={onRetry}
            retryLoading={retryLoading}
          />
        ) : (
          <DataTable
            title={<Text strong>{tableTitle}</Text>}
            data={data}
            columns={columns}
            loading={loading}
            rowKey={rowKey}
            rightActions={rightActions ?? defaultRightActions}
            searchInput={searchInput}
            renderEmpty={renderEmpty}
            testId={testId}
            scroll={{ x: "max-content" }}
          />
        )}
      </Content>
    </Layout>
  );
};
